import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import getUserColumns from 'data-frames/util/getUserColumns';
import {ColumnTypeValidationOptions, isValidColumnType} from 'data-frames/util/validateColumnType';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {OptionData} from 'ui-library/menus/MenuConstants';

export interface MultiSelectColumnDropdownProps {
  dataFrameSpec: Pick<DataFrameSpec, 'columns'>;
  /**
   * Optional column validation options. Columns that do not match these options will not be shown in the
   * selector.
   *
   * If not provided, all user-defined columns in the `dataFrameSpec` will be shown.
   */
  validationOptions?: ColumnTypeValidationOptions;

  selectedColumnSystemNames: Array<string>;
  onChange: (newColumnSystemNames: Array<string>) => void;

  /**
   * Text to display when the selected column is invalid. If the selected column does not conform to the
   * provided validationOptions, a "Invalid column selected" error will be displayed regardless of this prop.
   */
  error?: string;
  /**
   * Text to display when no column is selected.
   *  @default "None"
   */
  noColumnSelectedText?: string;
  buttonSize?: ButtonSize;
  buttonClassName?: string;
  disabled?: boolean;
  hideTypeIcon?: boolean;

  dataTestAttrs?: DataTestAttrs;
}

/**
 * Component that renders a dropdown selector and enables the user to select multiple columns within 1 data frame
 *
 * See also:
 *   * `ColumnDropdownSelector` - component that renders a dropdown selector and enables the user to select 1 column within 1 data frame
 *     * Path: client/data-frames/column-selector/ColumnDropdownSelector.tsx
 */
export default function MultiSelectColumnDropdown(props: MultiSelectColumnDropdownProps): JSX.Element {
  const allDataFrameColumns = getUserColumns(props.dataFrameSpec);
  const allColumnsBySystemName = objectFromEntries(allDataFrameColumns.map((c) => [c.systemName, c]));
  const validationOptions = props.validationOptions ?? {};

  const selectedColumns = props.selectedColumnSystemNames.map(
    (systemName) => allColumnsBySystemName[systemName]
  );

  const selectableColumns = allDataFrameColumns.filter((column) =>
    isValidColumnType(column.type, validationOptions)
  );
  const selectableColumnSystemNames = new Set(selectableColumns.map((column) => column.systemName));
  const optionDatas: Array<OptionData> = selectableColumns.map((column) => ({
    icon: props.hideTypeIcon ? undefined : columnToTypeIcon(column),
    name: column.displayName,
    value: column.systemName,
  }));

  return (
    <MultiSelectSearchableDropdown
      buttonSize={props.buttonSize}
      buttonClassName={props.buttonClassName}
      disabled={props.disabled}
      error={
        selectedColumns.some((column) => column == null)
          ? 'Column does not exist'
          : selectedColumns.some((column) => !isValidColumnType(column.type, validationOptions))
            ? 'Invalid column selected'
            : props.error
      }
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      optionDatas={optionDatas}
      onSelect={(newColumnSystemNames): void => {
        // If the component is initially provided system names that either don't exist on the data
        // frame or aren't valid, we need to remove them here
        props.onChange(
          newColumnSystemNames.filter((systemName) => selectableColumnSystemNames.has(systemName))
        );
      }}
      noneSelectedMessage={props.noColumnSelectedText ?? 'None'}
      renderSelectedValues={(systemNames) =>
        systemNames.map((systemName) => (
          <Chip
            size={ChipSize.SMALL}
            icon={
              props.hideTypeIcon
                ? null
                : allColumnsBySystemName[systemName] != null
                  ? columnToTypeIcon(allColumnsBySystemName[systemName])
                  : 'none-option'
            }
            text={allColumnsBySystemName[systemName]?.displayName ?? systemName}
            isReadonly={true}
            key={systemName}
          />
        ))
      }
      selectedValues={props.selectedColumnSystemNames}
      tooltipToggleDisplayMode={ToggleDisplayMode.BLOCK}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}
